import styled from '@emotion/styled';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    PageContentHorizontalAlignment,
    PageContentVerticalAlignment,
} from 'src/generated/webapp_gql';
import { Button, pxToRem } from '@modernhealth/vital-components';

export interface VerticalAlignmentProps {
    contentVerticalAlignment?: PageContentVerticalAlignment;
}
export interface HorizontalAlignmentProps {
    contentHorizontalAlignment: PageContentHorizontalAlignment;
}

const Content = styled.div<VerticalAlignmentProps>(
    {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
    },
    ({ contentVerticalAlignment }) => {
        switch (contentVerticalAlignment) {
            case PageContentVerticalAlignment.Top:
                return {
                    justifyContent: 'flex-start',
                };
            case PageContentVerticalAlignment.Middle:
            default:
                return {
                    justifyContent: 'center',
                };
        }
    },
);

const PageContainer = styled.div({
    marginTop: 30,
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    padding: '0 16px',
    flexDirection: 'column',
});

const NavigationContainer = styled.div<{ justifyNavigation: PageProps['justifyNavigation'] }>(
    ({ justifyNavigation }) => ({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: justifyNavigation,
        marginTop: 40,
    }),
);

const NavigationButton = styled(Button)({
    '&&': {
        minWidth: 241,
        maxWidth: 241,
    },
});

interface PageNavigationProps {
    onNavigateNext: VoidFunction;
    onNavigatePrev?: VoidFunction;
    justifyNavigation?: 'flex-start' | 'center';
}

export interface PageNavigationComponentProps extends PageNavigationProps {
    defaultComponent: React.FC<PageNavigationProps>;
}

export interface PageProps extends VerticalAlignmentProps, PageNavigationProps {
    navigationComponent?: React.FC<PageNavigationComponentProps>;
}

const ResolvedContainer: React.FC<VerticalAlignmentProps> = ({
    children,
    contentVerticalAlignment,
}) => {
    return (
        <PageContainer>
            <Content contentVerticalAlignment={contentVerticalAlignment}>{children}</Content>
        </PageContainer>
    );
};

const PageDefaultNavigation: FC<PageNavigationComponentProps> = ({
    justifyNavigation,
    onNavigateNext,
    onNavigatePrev,
}) => {
    const { t } = useTranslation();

    return (
        <NavigationContainer justifyNavigation={justifyNavigation}>
            {onNavigatePrev && (
                <NavigationButton variant="secondary" onClick={onNavigatePrev}>
                    {t('common.back')}
                </NavigationButton>
            )}
            <NavigationButton
                variant="primary"
                onClick={onNavigateNext}
                style={{ marginLeft: onNavigatePrev ? pxToRem(16) : 0 }}
            >
                {t('common.continue')}
            </NavigationButton>
        </NavigationContainer>
    );
};

export const Page: FC<PageProps> = ({
    onNavigateNext,
    onNavigatePrev,
    children,
    justifyNavigation = 'flex-start',
    contentVerticalAlignment = PageContentVerticalAlignment.Middle,
    navigationComponent: NavigationComponent = PageDefaultNavigation,
}) => {
    return (
        <ResolvedContainer contentVerticalAlignment={contentVerticalAlignment}>
            {children}
            <NavigationComponent
                onNavigateNext={onNavigateNext}
                onNavigatePrev={onNavigatePrev}
                justifyNavigation={justifyNavigation}
                defaultComponent={PageDefaultNavigation}
            />
        </ResolvedContainer>
    );
};
